/**
 * Playback controller for the product video carousel.
 *
 * The markup ships every video with preload="none" and a poster frame, so an
 * eight-slide carousel costs eight images on load rather than eight videos.
 * This element decides which of them actually stream:
 *
 *   - a card within 300px of the viewport is warmed (preload switched on) so
 *     playback starts without a visible stall once it scrolls in;
 *   - a card at least 60% visible plays; anything else pauses.
 *
 * Deliberately dependency-free rather than extending @theme/component: it needs
 * no ref plumbing, and staying off the import map means the carousel keeps
 * working if the section is copied into another theme.
 */

const VISIBLE_ENOUGH_TO_PLAY = 0.6;
const WARM_UP_MARGIN = '300px';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class CapVideoCarousel extends HTMLElement {
  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('[data-cap-video-card]'));
    if (this.cards.length === 0) return;

    this.warmObserver = new IntersectionObserver((entries) => this.#warm(entries), {
      rootMargin: WARM_UP_MARGIN,
    });

    this.playObserver = new IntersectionObserver((entries) => this.#syncPlayback(entries), {
      threshold: [0, VISIBLE_ENOUGH_TO_PLAY],
    });

    for (const card of this.cards) {
      this.warmObserver.observe(card);
      this.playObserver.observe(card);

      const toggle = card.querySelector('[data-cap-video-toggle]');
      toggle?.addEventListener('click', () => this.#toggle(card));

      // A video that ends up paused for any reason still needs its button to
      // read correctly, and the browser pauses on its own for things this
      // element never sees (data saver, low power mode, tab discard).
      const video = card.querySelector('video');
      video?.addEventListener('play', () => this.#paintToggle(card, true));
      video?.addEventListener('pause', () => this.#paintToggle(card, false));
      // Capture phase so this also sees error events from the <source> children.
      video?.addEventListener('error', () => this.#fallBackFromHls(card), true);
    }

    this.#onMotionPreferenceChange = () => {
      if (reducedMotion.matches) this.cards.forEach((card) => this.#pause(card));
    };
    reducedMotion.addEventListener('change', this.#onMotionPreferenceChange);

    // Backgrounded tabs keep playing in some browsers; there is no point
    // burning bandwidth on a carousel nobody is looking at.
    this.#onVisibilityChange = () => {
      if (document.hidden) this.cards.forEach((card) => this.#pause(card));
    };
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
  }

  disconnectedCallback() {
    this.warmObserver?.disconnect();
    this.playObserver?.disconnect();
    if (this.#onMotionPreferenceChange) {
      reducedMotion.removeEventListener('change', this.#onMotionPreferenceChange);
    }
    if (this.#onVisibilityChange) {
      document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    }
  }

  #onMotionPreferenceChange = null;
  #onVisibilityChange = null;

  #warm(entries) {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;

      const video = entry.target.querySelector('video');
      if (video && video.preload === 'none') {
        video.preload = 'auto';
        video.load();
      }

      // Warming is a one-way trip - once the bytes are on the wire there is
      // nothing to undo, so stop watching this card.
      this.warmObserver.unobserve(entry.target);
    }
  }

  #syncPlayback(entries) {
    for (const entry of entries) {
      const card = entry.target;

      if (entry.intersectionRatio >= VISIBLE_ENOUGH_TO_PLAY) {
        // Honour an explicit pause: scrolling a card back into view should not
        // undo the visitor's decision to stop it.
        if (card.dataset.userPaused === 'true') continue;
        if (reducedMotion.matches) continue;
        this.#play(card);
      } else {
        this.#pause(card);
      }
    }
  }

  #toggle(card) {
    const video = card.querySelector('video');
    if (!video) return;

    if (video.paused) {
      card.dataset.userPaused = 'false';
      this.#play(card);
    } else {
      card.dataset.userPaused = 'true';
      this.#pause(card);
    }
  }

  #play(card) {
    const video = card.querySelector('video');
    if (!video || !video.paused) return;

    // Autoplay is refused often enough (low power mode, a muted attribute lost
    // to a bad edit) that an unhandled rejection here would be routine noise.
    video.play().catch(() => {
      this.#paintToggle(card, false);
    });
  }

  #pause(card) {
    const video = card.querySelector('video');
    if (video && !video.paused) video.pause();
  }

  /**
   * The card lists its HLS source first so Safari and iOS get adaptive bitrate
   * instead of a fixed 1080p download. Source selection is one-shot, though: a
   * browser that advertises HLS ("maybe" from canPlayType) but then fails to
   * decode it would leave a dead video with no second chance. Drop the HLS
   * source and reload onto the mp4.
   */
  #fallBackFromHls(card) {
    if (card.dataset.hlsFallback === 'done') return;

    const video = card.querySelector('video');
    const hlsSource = video?.querySelector('source[type="application/x-mpegURL"]');
    if (!hlsSource) return;

    card.dataset.hlsFallback = 'done';
    hlsSource.remove();
    video.load();
    if (card.dataset.userPaused !== 'true') this.#play(card);
  }

  #paintToggle(card, playing) {
    const toggle = card.querySelector('[data-cap-video-toggle]');
    if (!toggle) return;

    toggle.setAttribute('aria-label', playing ? toggle.dataset.labelPause : toggle.dataset.labelPlay);
    toggle.querySelector('.cap-video-card__icon--pause')?.classList.toggle('hidden', !playing);
    toggle.querySelector('.cap-video-card__icon--play')?.classList.toggle('hidden', playing);
  }
}

if (!customElements.get('cap-video-carousel')) {
  customElements.define('cap-video-carousel', CapVideoCarousel);
}
